import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import {
  FaArrowLeft,
  FaBed,
  FaCloudDownloadAlt,
  FaCloudUploadAlt,
  FaFileExcel,
  FaUsers,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaSearch,
  FaDownload,
} from "react-icons/fa";

import API from "./config/API";

function ExcelSheetUploadDownload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // =========================================================
  // API
  // =========================================================

  const AVAILABLE_BEDS_API = `${API.V2}/pgOwner/getAllAvailableBedsinPg`;

  /*
   * Change this endpoint if your Spring Boot backend
   * uses another URL for bulk Excel upload.
   */
  const BULK_USERS_EXCEL_API = `${API.V2}/excel/bulkUsersByExcel`;

  // =========================================================
  // STATE
  // =========================================================

  const [availableBeds, setAvailableBeds] = useState([]);

  const [loadingBeds, setLoadingBeds] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);

  const [excelRows, setExcelRows] = useState([]);

  const [search, setSearch] = useState("");

  const [selectedFloor, setSelectedFloor] = useState("");

  const [toast, setToast] = useState({
    show: false,
    type: "",
    message: "",
  });

  // =========================================================
  // TOAST
  // =========================================================

  const showToast = (message, type = "success") => {
    setToast({
      show: true,
      type,
      message,
    });

    setTimeout(() => {
      setToast({
        show: false,
        type: "",
        message: "",
      });
    }, 3000);
  };

  // =========================================================
  // LOGIN USER
  // =========================================================

  const getLoggedUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (error) {
      return {};
    }
  };

  // =========================================================
  // LOAD AVAILABLE BEDS
  // =========================================================

  useEffect(() => {
    loadAvailableBeds();
  }, []);

  const loadAvailableBeds = async () => {
    try {
      setLoadingBeds(true);

      const user = getLoggedUser();

      const requestBody = {
        emailId: user?.emailId || "",
      };

      const response = await axios.post(
        AVAILABLE_BEDS_API,
        {
          emailId: user?.emailId || "",
        },
        {
          headers: {
            Authorization: `Bearer ${user?.jwtToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      if (response.data?.statusCode === 200) {
        const beds = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        setAvailableBeds(beds);

        if (beds.length === 0) {
          showToast("No available beds found.", "error");
        }
      } else {
        setAvailableBeds([]);

        showToast(
          response.data?.message || "No available beds found.",
          "error",
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        alert("Session expired. Please login again.");

        navigate("/", {
          replace: true,
        });

        return;
      }

      console.error("Available Beds Error:", error.response?.data || error);

      setAvailableBeds([]);

      showToast(
        error.response?.data?.message || "Unable to load available beds.",
        "error",
      );
    } finally {
      setLoadingBeds(false);
    }
  };
  // =========================================================
  // FLOOR LIST
  // =========================================================

  const floors = useMemo(() => {
    return [
      ...new Set(availableBeds.map((bed) => bed.floorName).filter(Boolean)),
    ];
  }, [availableBeds]);

  // =========================================================
  // FILTER BED DATA
  // =========================================================

  const filteredBeds = useMemo(() => {
    const searchText = search.toLowerCase().trim();

    return availableBeds.filter((bed) => {
      const matchesSearch =
        !searchText ||
        bed.bedName?.toLowerCase().includes(searchText) ||
        bed.roomName?.toLowerCase().includes(searchText) ||
        bed.floorName?.toLowerCase().includes(searchText);

      const matchesFloor = !selectedFloor || bed.floorName === selectedFloor;

      return matchesSearch && matchesFloor;
    });
  }, [availableBeds, search, selectedFloor]);

  // =========================================================
  // EXCEL TEMPLATE COLUMNS
  // =========================================================

  const createExcelRows = (beds = availableBeds) => {
    return beds.map((bed) => ({
      BedId: bed.bedId ?? "",
      BedName: bed.bedName ?? "",
      FloorId: bed.floorId ?? "",
      FloorName: bed.floorName ?? "",
      RoomId: bed.roomId ?? "",
      RoomName: bed.roomName ?? "",
      BedSharingPerRoom: bed.bedSharingPerRoom ?? "",
      BedCost: bed.bedCost ?? "",
      OfferPrice: bed.inOfferPrice_bedCost ?? "",
      BedCostPerDay: bed.bedCostPerDay ?? "",
      SnoringRoom: bed.isSnoringroom ? "YES" : "NO",

      // =====================================================
      // USER DETAILS
      // =====================================================

      UserName: "",
      EmailId: "",
      ContactNo: "",
      AlternateContactNo: "",
      Gender: "",
      AadhaarNo: "",
      DateOfBirth: "",
      JoiningDate: "",

      // =====================================================
      // STAYING
      // =====================================================

      StayingType: "MONTH",
      Duration: 1,

      // =====================================================
      // PAYMENT
      // =====================================================

      PaymentMode: "ONLINE",
      PaymentAmount: "",
      PaymentReferenceNo: "",
      PaymentDate: "",
    }));
  };

  // =========================================================
  // DOWNLOAD EXCEL
  // =========================================================

  const downloadExcel = (rows = createExcelRows()) => {
    try {
      if (!rows || rows.length === 0) {
        showToast("No available bed records to download.", "error");

        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "BulkUsers");

      // Column widths
      worksheet["!cols"] = [
        { wch: 10 },
        { wch: 17 },
        { wch: 10 },
        { wch: 14 },
        { wch: 10 },
        { wch: 14 },
        { wch: 20 },
        { wch: 13 },
        { wch: 13 },
        { wch: 15 },
        { wch: 14 },

        { wch: 22 },
        { wch: 30 },
        { wch: 17 },
        { wch: 20 },
        { wch: 12 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },

        { wch: 15 },
        { wch: 12 },

        { wch: 16 },
        { wch: 22 },
        { wch: 25 },
        { wch: 18 },
      ];

      XLSX.writeFile(workbook, "SVK_PG_Bulk_Users_Template.xlsx");

      showToast("Excel template downloaded successfully.", "success");
    } catch (error) {
      console.error("Excel download error:", error);

      showToast("Unable to download Excel.", "error");
    }
  };

  // =========================================================
  // DOWNLOAD ONLY AVAILABLE BED RECORDS
  // =========================================================

  const downloadAvailableBeds = () => {
    const rows = createExcelRows(filteredBeds);

    downloadExcel(rows);
  };

  // =========================================================
  // OPEN FILE PICKER
  // =========================================================

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // =========================================================
  // READ EXCEL
  // =========================================================

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
      showToast("Please select an Excel file (.xlsx or .xls).", "error");

      event.target.value = "";

      return;
    }

    try {
      setSelectedFile(file);

      const arrayBuffer = await file.arrayBuffer();

      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
      });

      const firstSheetName = workbook.SheetNames[0];

      if (!firstSheetName) {
        showToast("Excel sheet is empty.", "error");

        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: false,
      });

      if (!rows.length) {
        setExcelRows([]);

        showToast("No records found in Excel file.", "error");

        return;
      }

      // Validate required columns
      const requiredColumns = [
        "BedId",
        "UserName",
        "EmailId",
        "ContactNo",
        "JoiningDate",
        "StayingType",
        "Duration",
        "PaymentMode",
        "PaymentAmount",
      ];

      const headers = Object.keys(rows[0]);

      const missingColumns = requiredColumns.filter(
        (column) => !headers.includes(column),
      );

      if (missingColumns.length > 0) {
        setExcelRows([]);

        showToast(`Missing columns: ${missingColumns.join(", ")}`, "error");

        return;
      }

      setExcelRows(rows);

      showToast(
        `${rows.length} Excel record(s) loaded successfully.`,
        "success",
      );
    } catch (error) {
      console.error("Excel read error:", error);

      setExcelRows([]);

      showToast("Unable to read Excel file.", "error");
    }
  };

  // =========================================================
  // VALIDATE EXCEL DATA
  // =========================================================

  const validateExcelRows = () => {
    if (!excelRows.length) {
      return {
        valid: false,
        message: "Please upload an Excel file first.",
      };
    }

    const availableBedIds = new Set(
      availableBeds.map((bed) => String(bed.bedId)),
    );

    const usedBedIds = new Set();

    for (let i = 0; i < excelRows.length; i++) {
      const row = excelRows[i];

      const rowNumber = i + 2;

      // ==============================================
      // BED
      // ==============================================

      if (!String(row.BedId || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: BedId is required.`,
        };
      }

      const bedId = String(row.BedId).trim();

      if (!availableBedIds.has(bedId)) {
        return {
          valid: false,
          message: `Row ${rowNumber}: BedId ${bedId} is not currently available.`,
        };
      }

      if (usedBedIds.has(bedId)) {
        return {
          valid: false,
          message: `Row ${rowNumber}: BedId ${bedId} is duplicated.`,
        };
      }

      usedBedIds.add(bedId);

      // ==============================================
      // USER
      // ==============================================

      if (!String(row.UserName || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: UserName is required.`,
        };
      }

      if (!String(row.EmailId || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: EmailId is required.`,
        };
      }

      if (!String(row.ContactNo || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: ContactNo is required.`,
        };
      }

      // ==============================================
      // JOINING
      // ==============================================

      if (!String(row.JoiningDate || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: JoiningDate is required.`,
        };
      }

      // ==============================================
      // STAYING
      // ==============================================

      if (!String(row.StayingType || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: StayingType is required.`,
        };
      }

      const duration = Number(row.Duration);

      if (!duration || duration <= 0) {
        return {
          valid: false,
          message: `Row ${rowNumber}: Duration must be greater than 0.`,
        };
      }

      // ==============================================
      // PAYMENT
      // ==============================================

      if (!String(row.PaymentMode || "").trim()) {
        return {
          valid: false,
          message: `Row ${rowNumber}: PaymentMode is required.`,
        };
      }

      const paymentAmount = Number(row.PaymentAmount);

      if (Number.isNaN(paymentAmount) || paymentAmount < 0) {
        return {
          valid: false,
          message: `Row ${rowNumber}: PaymentAmount is invalid.`,
        };
      }
    }

    return {
      valid: true,
      message: "Excel validation successful.",
    };
  };

  // =========================================================
  // UPLOAD EXCEL TO BACKEND
  // =========================================================

  const uploadExcel = async () => {
    const validation = validateExcelRows();

    if (!validation.valid) {
      showToast(validation.message, "error");

      return;
    }

    if (!selectedFile) {
      showToast("Please select an Excel file.", "error");

      return;
    }

    try {
      setUploading(true);

      const user = getLoggedUser();

      /*
       * Multipart request
       *
       * Backend example:
       *
       * @PostMapping("/bulkUsersByExcel")
       * public PgOwnerResponse bulkUsersByExcel(
       *      @RequestParam("file") MultipartFile file
       * )
       */

      const formData = new FormData();

      formData.append("file", selectedFile);

      const response = await axios.post(BULK_USERS_EXCEL_API, formData, {
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          Accept: "application/json",
        },
      });

      if (response.data?.statusCode === 200) {
        showToast(
          response.data?.message || "Bulk users uploaded successfully.",
          "success",
        );

        setSelectedFile(null);
        setExcelRows([]);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Refresh available beds
        await loadAvailableBeds();
      } else {
        showToast(response.data?.message || "Bulk upload failed.", "error");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        alert("Session expired. Please login again.");

        navigate("/", {
          replace: true,
        });

        return;
      }

      console.error("Bulk Excel Upload Error:", error);

      showToast(
        error.response?.data?.message || "Unable to upload bulk users.",
        "error",
      );
    } finally {
      setUploading(false);
    }
  };

  // =========================================================
  // REMOVE SELECTED FILE
  // =========================================================

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setExcelRows([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div style={styles.page}>
      {/* =====================================================
          TOAST
      ====================================================== */}

      {toast.show && (
        <div
          style={{
            ...styles.toast,
            background:
              toast.type === "success"
                ? "linear-gradient(135deg,#059669,#10b981)"
                : "linear-gradient(135deg,#dc2626,#ef4444)",
          }}
        >
          <div style={styles.toastIcon}>
            {toast.type === "success" ? <FaCheckCircle /> : <FaTimesCircle />}
          </div>

          <span>{toast.message}</span>
        </div>
      )}

      {/* =====================================================
          HEADER
      ====================================================== */}

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
            Back
          </button>

          <div>
            <div style={styles.titleRow}>
              <div style={styles.titleIcon}>
                <FaFileExcel />
              </div>

              <div>
                <h1 style={styles.title}>Bulk Users by Excel</h1>

                <p style={styles.subtitle}>
                  Download available beds, enter user & payment details, and
                  upload multiple bookings.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.bedCount}>
          <FaBed />

          <div>
            <span>Available Beds</span>

            <strong>{availableBeds.length}</strong>
          </div>
        </div>
      </div>

      {/* =====================================================
          DOWNLOAD SECTION
      ====================================================== */}

      <div style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.sectionIconBlue}>
              <FaCloudDownloadAlt />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <h2 style={styles.sectionTitle}>1. Download Excel</h2>

            <p style={styles.sectionDescription}>
              Download the current available-bed records. User and payment
              columns are included for entering bulk booking information.
            </p>
          </div>
        </div>

        <div style={styles.downloadButtons}>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={() => downloadExcel()}
            disabled={loadingBeds || availableBeds.length === 0}
          >
            <FaFileExcel />
            Download Excel Template
          </button>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={downloadAvailableBeds}
            disabled={loadingBeds || filteredBeds.length === 0}
          >
            <FaDownload />
            Download Filtered Beds
          </button>
        </div>
      </div>

      {/* =====================================================
          AVAILABLE BEDS
      ====================================================== */}

      <div style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionIconGreen}>
            <FaBed />
          </div>

          <div style={{ flex: 1 }}>
            <h2 style={styles.sectionTitle}>2. Available Beds</h2>

            <p style={styles.sectionDescription}>
              These beds are currently available for bulk user registration.
            </p>
          </div>
        </div>

        {/* FILTER */}

        <div style={styles.filterBar}>
          <div style={styles.searchWrapper}>
            <FaSearch style={styles.searchIcon} />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bed, room or floor..."
              style={styles.searchInput}
            />
          </div>

          <select
            value={selectedFloor}
            onChange={(e) => setSelectedFloor(e.target.value)}
            style={styles.floorSelect}
          >
            <option value="">All Floors</option>

            {floors.map((floor) => (
              <option key={floor} value={floor}>
                {floor}
              </option>
            ))}
          </select>
        </div>

        {/* BED TABLE */}

        <div style={styles.tableWrapper}>
          {loadingBeds ? (
            <div style={styles.loadingBox}>
              <FaSpinner style={styles.spinningIcon} />
              Loading available beds...
            </div>
          ) : filteredBeds.length === 0 ? (
            <div style={styles.emptyBox}>
              <FaBed />

              <strong>No available beds found</strong>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>S.No</th>

                  <th style={styles.th}>Bed</th>

                  <th style={styles.th}>Floor</th>

                  <th style={styles.th}>Room</th>

                  <th style={styles.th}>Sharing</th>

                  <th style={styles.th}>Bed Cost</th>

                  <th style={styles.th}>Offer Price</th>

                  <th style={styles.th}>Snoring</th>
                </tr>
              </thead>

              <tbody>
                {filteredBeds.map((bed, index) => (
                  <tr key={bed.bedId}>
                    <td style={styles.td}>{index + 1}</td>

                    <td
                      style={{
                        ...styles.td,
                        fontWeight: 700,
                        color: "#1d4ed8",
                      }}
                    >
                      {bed.bedName}
                    </td>

                    <td style={styles.td}>{bed.floorName}</td>

                    <td style={styles.td}>{bed.roomName}</td>

                    <td style={styles.td}>{bed.bedSharingPerRoom}</td>

                    <td style={styles.td}>
                      ₹{Number(bed.bedCost || 0).toLocaleString("en-IN")}
                    </td>

                    <td style={styles.td}>
                      ₹
                      {Number(bed.inOfferPrice_bedCost || 0).toLocaleString(
                        "en-IN",
                      )}
                    </td>

                    <td style={styles.td}>
                      {bed.isSnoringroom ? (
                        <span style={styles.snoringYes}>Yes</span>
                      ) : (
                        <span style={styles.snoringNo}>No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* =====================================================
          UPLOAD SECTION
      ====================================================== */}

      <div style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionIconPurple}>
            <FaCloudUploadAlt />
          </div>

          <div style={{ flex: 1 }}>
            <h2 style={styles.sectionTitle}>3. Upload Bulk Users</h2>

            <p style={styles.sectionDescription}>
              Enter user and payment details in the downloaded Excel file and
              upload it here.
            </p>
          </div>
        </div>

        {/* FILE INPUT */}

        <div style={styles.uploadArea}>
          <FaCloudUploadAlt style={styles.uploadLargeIcon} />

          <h3 style={styles.uploadTitle}>Upload Excel Sheet</h3>

          <p style={styles.uploadDescription}>Supported formats: .xlsx, .xls</p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{
              display: "none",
            }}
          />

          <button
            type="button"
            style={styles.selectFileButton}
            onClick={openFilePicker}
            disabled={uploading}
          >
            <FaFileExcel />
            Select Excel File
          </button>

          {selectedFile && (
            <div style={styles.selectedFile}>
              <div style={styles.selectedFileIcon}>
                <FaFileExcel />
              </div>

              <div style={styles.selectedFileText}>
                <strong>{selectedFile.name}</strong>

                <span>{(selectedFile.size / 1024).toFixed(1)} KB</span>
              </div>

              <button
                type="button"
                style={styles.removeFileButton}
                onClick={clearSelectedFile}
                disabled={uploading}
              >
                <FaTimesCircle />
              </button>
            </div>
          )}
        </div>

        {/* EXCEL PREVIEW */}

        {excelRows.length > 0 && (
          <div style={styles.previewSection}>
            <div style={styles.previewHeader}>
              <div>
                <h3 style={styles.previewTitle}>Excel Preview</h3>

                <span style={styles.previewCount}>
                  {excelRows.length} record(s)
                </span>
              </div>

              <div style={styles.validBadge}>
                <FaCheckCircle />
                Loaded
              </div>
            </div>

            <div style={styles.previewTableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>

                    <th style={styles.th}>Bed</th>

                    <th style={styles.th}>User Name</th>

                    <th style={styles.th}>Email</th>

                    <th style={styles.th}>Contact</th>

                    <th style={styles.th}>Joining Date</th>

                    <th style={styles.th}>Staying</th>

                    <th style={styles.th}>Payment</th>

                    <th style={styles.th}>Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {excelRows.slice(0, 20).map((row, index) => (
                    <tr key={index}>
                      <td style={styles.td}>{index + 1}</td>

                      <td style={styles.td}>
                        {row.BedName || row.BedId || "-"}
                      </td>

                      <td style={styles.td}>{row.UserName || "-"}</td>

                      <td style={styles.td}>{row.EmailId || "-"}</td>

                      <td style={styles.td}>{row.ContactNo || "-"}</td>

                      <td style={styles.td}>{row.JoiningDate || "-"}</td>

                      <td style={styles.td}>
                        {row.StayingType || "-"} {row.Duration || ""}
                      </td>

                      <td style={styles.td}>{row.PaymentMode || "-"}</td>

                      <td style={styles.td}>₹{row.PaymentAmount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {excelRows.length > 20 && (
              <p style={styles.moreRowsText}>
                Showing first 20 records from {excelRows.length} uploaded
                records.
              </p>
            )}
          </div>
        )}

        {/* UPLOAD BUTTON */}

        <div style={styles.uploadFooter}>
          <div>
            <div style={styles.requiredTitle}>Required Excel fields</div>

            <div style={styles.requiredFields}>
              BedId • UserName • EmailId • ContactNo • JoiningDate • StayingType
              • Duration • PaymentMode • PaymentAmount
            </div>
          </div>

          <button
            type="button"
            style={{
              ...styles.uploadButton,
              ...(uploading ? styles.disabledButton : {}),
            }}
            onClick={uploadExcel}
            disabled={uploading || !selectedFile || excelRows.length === 0}
          >
            {uploading ? (
              <>
                <FaSpinner style={styles.spinner} />
                Uploading...
              </>
            ) : (
              <>
                <FaCloudUploadAlt />
                Upload Bulk Users
              </>
            )}
          </button>
        </div>
      </div>

      {/* =====================================================
          INFORMATION
      ====================================================== */}

      <div style={styles.infoCard}>
        <FaUsers style={styles.infoIcon} />

        <div>
          <strong style={styles.infoTitle}>Bulk Registration</strong>

          <p style={styles.infoText}>
            Use the downloaded Excel template to register multiple users and
            assign them to currently available beds. Each BedId must be unique
            within the uploaded file.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ExcelSheetUploadDownload;

/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight: "100vh",
    padding: "30px",
    boxSizing: "border-box",
    background: "linear-gradient(135deg,#f8fafc 0%,#eff6ff 48%,#eef2ff 100%)",
    fontFamily: '"Inter","Segoe UI",Arial,sans-serif',
    color: "#0f172a",
  },

  /* =======================================================
     HEADER
  ======================================================== */

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "25px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
  },

  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "11px 15px",
    border: "1px solid #cbd5e1",
    borderRadius: "11px",
    background: "#ffffff",
    color: "#475569",
    fontWeight: "700",
    cursor: "pointer",
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  titleIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: "23px",
    background: "linear-gradient(135deg,#15803d,#22c55e)",
    boxShadow: "0 10px 25px rgba(34,197,94,.2)",
  },

  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "800",
    letterSpacing: "-.5px",
  },

  subtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  bedCount: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "13px 18px",
    borderRadius: "14px",
    background: "#ffffff",
    border: "1px solid #dbeafe",
    color: "#2563eb",
    boxShadow: "0 8px 25px rgba(15,23,42,.06)",
  },

  /* =======================================================
     SECTION
  ======================================================== */

  sectionCard: {
    background: "#ffffff",
    border: "1px solid rgba(226,232,240,.9)",
    borderRadius: "20px",
    padding: "24px",
    marginBottom: "22px",
    boxShadow: "0 12px 35px rgba(15,23,42,.07)",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "15px",
    marginBottom: "20px",
  },

  sectionIconBlue: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2563eb",
    background: "#dbeafe",
    fontSize: "18px",
  },

  sectionIconGreen: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#15803d",
    background: "#dcfce7",
    fontSize: "18px",
  },

  sectionIconPurple: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#7c3aed",
    background: "#ede9fe",
    fontSize: "18px",
  },

  sectionTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "800",
    color: "#1e293b",
  },

  sectionDescription: {
    margin: "5px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
    fontSize: "13px",
  },

  /* =======================================================
     BUTTONS
  ======================================================== */

  downloadButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },

  primaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    padding: "12px 18px",
    border: "none",
    borderRadius: "11px",
    background: "linear-gradient(135deg,#15803d,#16a34a)",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
  },

  secondaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    padding: "12px 18px",
    border: "1px solid #cbd5e1",
    borderRadius: "11px",
    background: "#ffffff",
    color: "#334155",
    fontWeight: "700",
    cursor: "pointer",
  },

  /* =======================================================
     FILTER
  ======================================================== */

  filterBar: {
    display: "flex",
    gap: "12px",
    marginBottom: "18px",
    flexWrap: "wrap",
  },

  searchWrapper: {
    flex: 1,
    minWidth: "240px",
    height: "46px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "0 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "11px",
    background: "#ffffff",
    boxSizing: "border-box",
  },

  searchIcon: {
    color: "#94a3b8",
  },

  searchInput: {
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    fontSize: "13px",
    color: "#0f172a",
  },

  floorSelect: {
    minWidth: "170px",
    height: "46px",
    padding: "0 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "11px",
    background: "#ffffff",
    color: "#334155",
    outline: "none",
    fontWeight: "600",
  },

  /* =======================================================
     TABLE
  ======================================================== */

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e2e8f0",
    borderRadius: "13px",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "850px",
  },

  th: {
    padding: "13px 12px",
    textAlign: "left",
    background: "#f8fafc",
    color: "#475569",
    fontSize: "11px",
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "13px 12px",
    fontSize: "12px",
    color: "#475569",
    borderBottom: "1px solid #f1f5f9",
    whiteSpace: "nowrap",
  },

  snoringYes: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "999px",
    background: "#fef2f2",
    color: "#dc2626",
    fontSize: "10px",
    fontWeight: "800",
  },

  snoringNo: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "999px",
    background: "#f0fdf4",
    color: "#15803d",
    fontSize: "10px",
    fontWeight: "800",
  },

  loadingBox: {
    minHeight: "160px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "#64748b",
    fontSize: "13px",
  },

  emptyBox: {
    minHeight: "160px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "8px",
    color: "#94a3b8",
  },

  spinningIcon: {
    animation: "excelSpin 1s linear infinite",
    color: "#2563eb",
  },

  /* =======================================================
     UPLOAD
  ======================================================== */

  uploadArea: {
    border: "2px dashed #c4b5fd",
    borderRadius: "18px",
    padding: "35px 25px",
    textAlign: "center",
    background: "linear-gradient(135deg,#faf5ff,#f8fafc)",
  },

  uploadLargeIcon: {
    fontSize: "42px",
    color: "#7c3aed",
  },

  uploadTitle: {
    margin: "12px 0 5px",
    fontSize: "17px",
    fontWeight: "800",
  },

  uploadDescription: {
    margin: "0 0 18px",
    color: "#94a3b8",
    fontSize: "12px",
  },

  selectFileButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    border: "none",
    borderRadius: "11px",
    background: "linear-gradient(135deg,#7c3aed,#8b5cf6)",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
  },

  selectedFile: {
    maxWidth: "550px",
    margin: "20px auto 0",
    padding: "12px",
    borderRadius: "12px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    textAlign: "left",
  },

  selectedFileIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#15803d",
    background: "#dcfce7",
  },

  selectedFileText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },

  removeFileButton: {
    border: "none",
    background: "transparent",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: "18px",
  },

  /* =======================================================
     PREVIEW
  ======================================================== */

  previewSection: {
    marginTop: "22px",
    padding: "18px",
    borderRadius: "15px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },

  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
  },

  previewTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: "800",
  },

  previewCount: {
    display: "block",
    marginTop: "3px",
    color: "#64748b",
    fontSize: "11px",
  },

  validBadge: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#dcfce7",
    color: "#15803d",
    fontSize: "11px",
    fontWeight: "800",
  },

  previewTableWrapper: {
    overflowX: "auto",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
  },

  moreRowsText: {
    margin: "10px 0 0",
    color: "#64748b",
    fontSize: "11px",
  },

  uploadFooter: {
    marginTop: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
  },

  requiredTitle: {
    fontSize: "12px",
    fontWeight: "800",
    color: "#334155",
  },

  requiredFields: {
    marginTop: "5px",
    maxWidth: "700px",
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.6,
  },

  uploadButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    minWidth: "210px",
    padding: "13px 20px",
    border: "none",
    borderRadius: "11px",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    color: "#ffffff",
    fontWeight: "800",
    cursor: "pointer",
    boxShadow: "0 10px 25px rgba(37,99,235,.25)",
  },

  disabledButton: {
    background: "#94a3b8",
    cursor: "not-allowed",
    boxShadow: "none",
  },

  spinner: {
    animation: "excelSpin 1s linear infinite",
  },

  /* =======================================================
     INFO
  ======================================================== */

  infoCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "13px",
    padding: "18px",
    borderRadius: "16px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
  },

  infoIcon: {
    color: "#2563eb",
    marginTop: "2px",
  },

  infoTitle: {
    color: "#1e40af",
    fontSize: "13px",
  },

  infoText: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  /* =======================================================
     TOAST
  ======================================================== */

  toast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 99999,
    maxWidth: "420px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "14px 18px",
    borderRadius: "13px",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "700",
    boxShadow: "0 15px 35px rgba(15,23,42,.22)",
  },

  toastIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
};

/* =========================================================
   ANIMATION
========================================================= */

if (
  typeof document !== "undefined" &&
  !document.getElementById("excel-upload-page-style")
) {
  const style = document.createElement("style");

  style.id = "excel-upload-page-style";

  style.innerHTML = `
    @keyframes excelSpin {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(360deg);
      }
    }

    @media (max-width: 768px) {

      body {
        overflow-x: hidden;
      }

      .excel-page {
        padding: 18px !important;
      }
    }
  `;

  document.head.appendChild(style);
}
